/**
 * The handover layer (`spec-onboarding-persistent-scene-handover`, R1): the held picture of the plan
 * map being left, shown above the pages until the next plan map has drawn, then faded out.
 *
 * Rendered once, in `ProtectedGate`'s provider stack beside the dialogs, so it stays mounted across
 * every route and step change (R1.1). It renders nothing while no picture is held. The picture is
 * placed at the viewport rectangle the leaving map's canvas occupied (R1.2), never takes pointer
 * input or focus and is hidden from assistive technology (R1.3), and sits at the tokens' `overlay`
 * layer: above page content, below a Modal, a toast and a tooltip.
 *
 * It has no look of its own: a picture of what was already on screen, with one opacity transition
 * at `motion.durFast` (R1.4). In the main chunk, so it imports no map or 3D library.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, zIndex } from '@repo/design-tokens';
import {
  FADE_MARGIN_MS,
  FADE_MS,
  FRAME_MS,
  fadeFor,
  finishRelease,
  getHandover,
  prefersReducedMotion,
  subscribeHandover,
  type HeldPicture,
} from './planHandover';

/** Re-exported for its tests: the fade's arithmetic lives with the hold (`planHandover.ts`). */
export { fadeFor };

const Layer = styled.div<{ $releasing: boolean; $hosted: boolean }>`
  position: ${p => (p.$hosted ? 'absolute' : 'fixed')};
  inset: 0;
  /* A hosted picture keeps its host's place in the page's stacking, under the chrome. */
  z-index: ${p => (p.$hosted ? 'auto' : zIndex.overlay)};
  pointer-events: none;
  overflow: hidden;
  opacity: ${p => (p.$releasing ? 0 : 1)};
  transition: opacity ${motion.durFast} ${motion.easeUi};
`;

/** A picture's host now: an element, a function's answer while it has none yet, or none. */
const resolveHost = (host: HeldPicture['host']): HTMLElement | null => {
  if (typeof host !== 'function') return host ?? null;
  try {
    const found = host();
    return found && found.isConnected ? found : null;
  } catch {
    return null;
  }
};

export const PlanHandoverLayer = () => {
  const held = useSyncExternalStore(subscribeHandover, getHandover, getHandover);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const picture = held?.picture ?? null;
  const releasing = held?.releasing ?? false;
  const immediate = held?.immediate ?? false;
  const fadeMs =
    held && held.releasedAt !== null ? fadeFor(held.heldAt, held.releasedAt, held.capMs) : FADE_MS;
  /*
   * A host found later (`spec-onboarding-persistent-scene-edit-flight`, R6.1): the live viewer's Edit
   * names onboarding's backdrop before onboarding has mounted. Until it exists the picture is held
   * over the page at the overlay layer; once it appears the picture moves into it, in the same place,
   * so onboarding's nav bar and step list draw above it. Watched only while such a picture is held.
   */
  const [, recheckHost] = useReducer((n: number) => n + 1, 0);
  const host = resolveHost(picture?.host);
  const waitingForHost = !!picture && typeof picture.host === 'function' && !host && !releasing;
  useEffect(() => {
    if (!waitingForHost || typeof MutationObserver === 'undefined') return undefined;
    const observer = new MutationObserver(() => {
      if (resolveHost(picture?.host)) recheckHost();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [waitingForHost, picture]);

  // R1.2: the canvas, placed where the leaving map's was. A layout effect, so it is in place before
  // the frame that first paints the new step (the hold and the step change share one commit).
  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer || !picture) return;
    const { canvas, rect } = picture;
    if (canvas.parentElement === layer) return;
    // Held in a host, the picture is placed in the host's own box.
    const origin = host ? host.getBoundingClientRect() : { left: 0, top: 0 };
    canvas.setAttribute('data-handover-picture', '');
    canvas.setAttribute('aria-hidden', 'true');
    Object.assign(canvas.style, {
      position: 'absolute',
      left: `${rect.left - origin.left}px`,
      top: `${rect.top - origin.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      pointerEvents: 'none',
    });
    layer.appendChild(canvas);
  }, [picture, host]);

  const finish = useCallback(() => {
    if (picture) finishRelease(picture);
  }, [picture]);

  // R1.4: released by fading out, then removed; at once under reduced motion or an immediate release.
  useLayoutEffect(() => {
    if (!picture || !releasing) return undefined;
    if (immediate || fadeMs < FRAME_MS || prefersReducedMotion()) {
      finishRelease(picture);
      return undefined;
    }
    const fallback = setTimeout(() => finishRelease(picture), fadeMs + FADE_MARGIN_MS);
    return () => clearTimeout(fallback);
  }, [picture, releasing, immediate, fadeMs]);

  if (!held) return null;
  const layer = (
    <Layer
      // A new picture gets a new layer, at full opacity with no fade in flight: one replacing a
      // picture still fading must not fade back in from part-transparent.
      key={held.pictureId}
      ref={layerRef}
      $releasing={releasing}
      $hosted={!!host}
      style={fadeMs < FADE_MS ? { transitionDuration: `${fadeMs}ms` } : undefined}
      aria-hidden="true"
      inert
      data-handover-layer=""
      onTransitionEnd={e => {
        if (e.target === e.currentTarget && e.propertyName === 'opacity' && releasing) finish();
      }}
    />
  );
  return host ? createPortal(layer, host) : layer;
};
